/**
 * Month-bounded booking dates (UTC, YYYY-MM-DD).
 * Public availability is month-based: check-in is the 1st, check-out is the last day.
 *
 * - Frontend: `@merlot/common/bookingMonthDates.js`
 * - Backend: mirror in `backend/src/utils/bookingMonthDates.js` (CommonJS)
 */

function pad2(n) {
  return String(n).padStart(2, '0');
}

function isYearMonth(year, month) {
  const y = Number(year);
  const m = Number(month);
  return Number.isInteger(y) && Number.isInteger(m) && m >= 1 && m <= 12;
}

/**
 * @param {number} year
 * @param {number} month - 1–12
 * @returns {string} YYYY-MM-01 or ''
 */
export function firstDayOfMonthIso(year, month) {
  if (!isYearMonth(year, month)) return '';
  return `${Number(year)}-${pad2(month)}-01`;
}

/**
 * Last UTC calendar day of a 1-indexed month.
 * Uses the Date object's UTC parts (not the input year/month) so December
 * cannot be formatted as YYYY-12-01 when the constructor overflows to January.
 *
 * @param {number} year
 * @param {number} month - 1–12
 * @returns {string} YYYY-MM-DD or ''
 */
export function lastDayOfMonthIso(year, month) {
  if (!isYearMonth(year, month)) return '';
  // Day 0 of monthIndex `month` is the last day of 1-indexed `month`.
  const last = new Date(Date.UTC(Number(year), Number(month), 0));
  return `${last.getUTCFullYear()}-${pad2(last.getUTCMonth() + 1)}-${pad2(last.getUTCDate())}`;
}

/**
 * @param {string} iso - YYYY-MM-DD
 * @returns {string} YYYY-MM-01 of that month, or ''
 */
export function toFirstDayOfMonthIso(iso) {
  if (!iso || typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  const [y, m] = iso.split('-').map(Number);
  return firstDayOfMonthIso(y, m);
}

/**
 * Snap any day in a month to that month's last UTC day.
 * Fixes public bookings that send YYYY-MM-01 as check-out (e.g. 2028-12-01 → 2028-12-31).
 *
 * @param {string} iso - YYYY-MM-DD
 * @returns {string} YYYY-MM-DD or ''
 */
export function toLastDayOfMonthIso(iso) {
  if (!iso || typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  const [y, m] = iso.split('-').map(Number);
  return lastDayOfMonthIso(y, m);
}

/**
 * Inclusive UTC month columns starting at startMonth0 (0 = January).
 * Year wrap uses (startMonth0 + i) / 12 so December → January increments the year.
 *
 * @param {number} startYear
 * @param {number} startMonth0 - 0–11
 * @param {number} count
 * @returns {Array<{ key: string, year: number, month: number, startIso: string, endIso: string, startDate: Date, endDate: Date }>}
 */
export function buildAvailabilityCalendarMonths(startYear, startMonth0, count) {
  const y0 = Number(startYear);
  const m0 = Number(startMonth0);
  const n = Number(count);
  if (!Number.isInteger(y0) || !Number.isInteger(m0) || m0 < 0 || m0 > 11) return [];
  if (!Number.isInteger(n) || n < 1) return [];

  const months = [];
  for (let i = 0; i < n; i++) {
    const month0 = (m0 + i) % 12;
    const year = y0 + Math.floor((m0 + i) / 12);
    const month = month0 + 1;
    months.push({
      key: `${year}-${pad2(month)}`,
      year,
      month,
      startIso: firstDayOfMonthIso(year, month),
      endIso: lastDayOfMonthIso(year, month),
      startDate: new Date(Date.UTC(year, month0, 1)),
      endDate: new Date(Date.UTC(year, month0 + 1, 0, 23, 59, 59, 999))
    });
  }
  return months;
}

/**
 * Ordered check-in / check-out ISO strings for a selected month index range.
 *
 * @param {Array<{ startIso?: string, endIso?: string }>} months
 * @param {number} startIndex
 * @param {number} endIndex
 * @returns {{ checkIn: string, checkOut: string } | null}
 */
export function selectedPeriodIso(months, startIndex, endIndex) {
  if (!Array.isArray(months) || months.length === 0) return null;
  const lo = Math.min(startIndex, endIndex);
  const hi = Math.max(startIndex, endIndex);
  const startMonth = months[lo];
  const endMonth = months[hi];
  if (!startMonth?.startIso || !endMonth?.endIso) return null;
  return {
    checkIn: startMonth.startIso,
    checkOut: endMonth.endIso
  };
}
